/* 分析工具 — 台美股市行事曆
 *
 *  內嵌「台美股市行事曆」網站（192.168.11.90:8813，Caddy 前置的 Vite/React
 *  單頁 SPA：整合台股與美股的經濟數據、財報與公司事件、除權息與休市日）。
 *  整站經 portal 後端唯讀反向代理 `/api/market-calendar/*`（同源，資產與
 *  `/api/events` 路徑由代理改寫掛前綴），前端不直連上游。
 *  整份照 hr-txo-board.jsx / hr-insider.jsx 複製、只改名；主要入口是分類頁
 *  「每日情報」／工作台卡片的「直開新分頁」（hr-shell.jsx openMarketCalendarTab），
 *  本 iframe 頁保留作 deep-link（#/market-calendar）與 Cmd+K 後備。
 *
 *  認證流程（iframe 內的資源與 fetch 帶不了 Authorization header，改用 cookie）：
 *    1. 掛載時先以 portal JWT `POST /api/market-calendar/session`（api() 會帶
 *       Authorization header），後端回 204 + Set-Cookie（HttpOnly、
 *       Path=/api/market-calendar、效期 8 小時）。
 *    2. cookie 落地後才渲染 iframe（src=/api/market-calendar/）—— iframe 內
 *       所有請求（含上游 SPA 的 /api/events fetch）同源自動帶這顆 cookie 過認證。
 *    3. cookie/JWT 過期時 iframe 內容會看到代理回的 401 —— 這裡不攔，
 *       使用者可回本頁按「重試」重新換一顆 session cookie。
 */

function MarketCalendarPage() {
  // 'loading' = 換 session 中；'ready' = cookie 已落地、渲染 iframe；'error' = 換發失敗
  const [phase, setPhase] = React.useState('loading');
  const [errDetail, setErrDetail] = React.useState('');

  const initSession = React.useCallback(() => {
    let alive = true;
    setPhase('loading');
    setErrDetail('');
    // 成功回 204（無 body）→ api() resolve null；非 2xx 會 throw { status, detail }
    api('POST', '/market-calendar/session')
      .then(() => { if (alive) setPhase('ready'); })
      .catch((e) => {
        if (!alive) return;
        setErrDetail((e && e.detail) || '網路連線失敗');
        setPhase('error');
      });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => initSession(), [initSession]);

  return (
    <>
      <PageHeader
        eyebrow="每日情報 · CALENDAR"
        title="台美股市行事曆"
        italic="台美股市行事曆 — 台股與美股的經濟數據、財報、除權息與休市日，經 portal 代理"
        action={
          // 同源開新分頁 —— session cookie（Path=/api/market-calendar）會自動帶上
          <Button
            variant="ghost" size="sm" icon="ext"
            onClick={() => window.open('/api/market-calendar/', '_blank', 'noopener')}
          >在新分頁開啟</Button>
        }
      />

      {phase === 'loading' && (
        <div style={uiStyle({
          height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: HR.surfaceAlt, borderRadius: 6, color: HR.inkFaint, fontSize: FontSizes.xs,
        }, { background: GxT.color.card, borderRadius: GxT.radius.card, color: GxT.color.onV, fontSize: 14 })}>
          <Spinner size={16} /> 連線台美股市行事曆中…
        </div>
      )}

      {phase === 'error' && (
        <Card padding="0">
          <EmptyState
            icon="x" title="台美股市行事曆連線失敗"
            hint={errDetail ? `無法建立代理連線（${errDetail}），請重試或聯繫管理員。` : '無法建立代理連線，請重試或聯繫管理員。'}
            action={<Button variant="primary" size="sm" icon="refresh" onClick={initSession}>重試</Button>}
          />
        </Card>
      )}

      {phase === 'ready' && (
        // 高度撐滿內容區：100vh 扣掉 WorkspaceHeader + main padding + PageHeader（比照 hr-insider.jsx）
        <iframe
          src="/api/market-calendar/"
          title="台美股市行事曆"
          style={uiStyle({
            width: '100%', height: 'calc(100vh - 260px)', minHeight: 480,
            border: 0, borderRadius: 6, background: HR.surface, display: 'block',
          }, { borderRadius: GxT.radius.card })}
        />
      )}
    </>
  );
}
